import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";
import FooterContact from "@/components/FooterContact";
import ThemeToggle from "@/components/ThemeToggle";
import SEO from "@/components/SEO";
import { useContact } from "@/components/ContactModal";
import { trackSeoLandingView, trackSeoLandingCta } from "@/pages/seo/track";

/**
 * Shared template for ClientFlow SEO landing pages.
 *
 * Renders the marketing nav + footer (matching Landing.jsx) and a
 * structured page body (hero → features → FAQ → CTA) driven by the
 * `content` object exported from `/pages/seo/content.js`.
 *
 * The template is intentionally a pure presentational component — all
 * copy lives in `content.js` so we can add or tune pages without
 * touching this file.
 *
 * It also emits a FAQPage JSON-LD block built from `content.faqs`, which
 * unlocks rich-result eligibility in Google for "People also ask"-style
 * answers.
 */
export default function SeoLandingPage({ content }) {
  const { open: openContact } = useContact();

  const slug = content.path.slice(1);
  const trackingBase = { slug, path: content.path, page_title: content.title };

  // Fire page-view event exactly once per slug. React StrictMode invokes
  // effects twice in development, so we guard with a ref keyed by slug.
  // In production this still ensures a single event even if React decides
  // to remount the component for any reason.
  const lastViewedSlugRef = useRef(null);
  useEffect(() => {
    if (lastViewedSlugRef.current === slug) return;
    lastViewedSlugRef.current = slug;
    trackSeoLandingView(trackingBase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Centralised CTA handler: optionally call openContact for "Book demo"
  // buttons, otherwise just record the click. The click itself always
  // proceeds via the Link/Button's own onClick — analytics never blocks.
  const handleCta = (cta, placement, opts = {}) => {
    trackSeoLandingCta({ ...trackingBase, cta, placement });
    if (opts.openContact) {
      openContact({ source: `seo-${slug}-${placement}` });
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background" data-testid={`seo-page-${slug}`}>
      <SEO
        title={content.title}
        description={content.description}
        path={content.path}
        jsonLd={faqJsonLd}
      />

      {/* Marketing nav — mirrors Landing.jsx so the brand stays consistent. */}
      <header className="sticky top-0 z-40 glass-nav border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="seo-nav-logo" aria-label="ClientFlow">
            <Logo className="h-8 sm:h-9 w-auto max-h-10" />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/#funksjoner" className="text-foreground/70 hover:text-foreground">
              Funksjoner
            </Link>
            <Link to="/#priser" className="text-foreground/70 hover:text-foreground">
              Priser
            </Link>
            <Link to="/book/nordlys-frisor" className="text-foreground/70 hover:text-foreground">
              Bookingflyt for kunder
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/logg-inn" className="hidden sm:block" onClick={() => handleCta("login", "nav")}>
              <Button variant="ghost" className="rounded-xl" data-testid="seo-nav-login-btn">
                Logg inn
              </Button>
            </Link>
            <Button
              onClick={() => handleCta("book_demo", "nav", { openContact: true })}
              className="rounded-xl bg-primary hover:bg-primary/90"
              data-testid="seo-nav-book-demo-btn"
            >
              Book demo
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden hero-grain">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 lg:pt-20 pb-14 lg:pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline mb-6"
            data-testid="seo-hero-back-link"
            onClick={() => handleCta("back_home", "hero")}
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
            Tilbake til forsiden
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold tracking-wide">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
            {content.heroEyebrow}
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05]">
            {content.h1}
          </h1>
          <p className="mt-5 text-lg text-muted-foreground max-w-2xl">{content.heroIntro}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              onClick={() => handleCta("book_demo", "hero", { openContact: true })}
              className="rounded-xl bg-primary hover:bg-primary/90 w-full sm:w-auto"
              data-testid="seo-hero-cta-demo"
            >
              Book demo
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
            </Button>
            <Link to="/#priser" onClick={() => handleCta("see_prices", "hero")}>
              <Button variant="outline" size="lg" className="rounded-xl w-full sm:w-auto" data-testid="seo-hero-cta-priser">
                Se priser
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 lg:py-20 bg-muted/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest font-semibold text-primary">Funksjoner</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">
              Slik fungerer det
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {content.features.map((f) => (
              <div
                key={f.title}
                className="bg-card rounded-2xl border border-border p-6 card-hover"
                data-testid={`seo-feature-${f.title}`}
              >
                <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center text-primary">
                  <f.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-xl">{f.title}</h3>
                <p className="mt-2 text-muted-foreground">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest font-semibold text-primary">FAQ</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">
              Vanlige spørsmål
            </h2>
          </div>
          <div className="mt-8 space-y-3" data-testid="seo-faq-list">
            {content.faqs.map((f, i) => (
              <details
                key={i}
                className="group bg-card rounded-2xl border border-border p-5 transition-all hover:border-primary/30"
                data-testid={`seo-faq-item-${i}`}
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-heading font-semibold">
                  <span>{f.q}</span>
                  <span className="text-muted-foreground transition-transform group-open:rotate-45 text-2xl leading-none select-none">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-14 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              {content.ctaTitle}
            </h2>
            <p className="mt-4 text-primary-foreground/80 max-w-xl mx-auto">{content.ctaText}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
              <Button
                size="lg"
                onClick={() => handleCta("book_demo", "final", { openContact: true })}
                className="rounded-xl bg-primary-foreground text-primary hover:bg-primary-foreground/90 w-full sm:w-auto"
                data-testid="seo-final-cta-demo"
              >
                Book demo
              </Button>
              <Link to="/#priser" onClick={() => handleCta("see_prices", "final")}>
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-xl w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  data-testid="seo-final-cta-priser"
                >
                  Se priser
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer — matches Landing.jsx */}
      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Logo className="h-6 w-auto" />
            <span className="ml-2">© 2026 ClientFlow</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/vilkar" className="hover:text-foreground" data-testid="seo-footer-link-vilkar">
              Vilkår
            </Link>
            <Link to="/personvern" className="hover:text-foreground" data-testid="seo-footer-link-personvern">
              Personvern
            </Link>
            <FooterContact />
          </div>
        </div>
      </footer>
    </div>
  );
}
